import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { Button, DefinitionLabel, DefinitionValue, Divider, IconButton } from '@cargomart/ui'
import { useNavigate } from '@tanstack/react-router'
import { useDownloadAssignmentQuery, useDownloadInvoiceQuery } from 'api/documentApi'
import IconClose from 'assets/close.svg?react'
import IconCopy from 'assets/file-copy-line.svg?react'
import IconModalClose from 'assets/modal-close.svg?react'
import cn from 'classnames'
import { Seo } from 'components'
import { Collapse } from 'components/Collapse'
import { ButtonIcon } from 'components/extend/ButtonIcon'
import { ModalDesktop, ModalHeader, ModalMobile } from 'components/extend/Modal'
import { notify } from 'components/extend/Notification/NotificationEmitter'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { ModalSimple } from 'components/ModalSimple'
import { getEnv } from 'helpers/common'
import { useOnMount } from 'hooks'
import { useIsLargeLaptop, useIsMobile, useIsTablet } from 'hooks/media'
import some from 'lodash/some'
import {
  useCancelOrderMutation,
  useFindCheckOutScheduleQuery,
  useFindOrderPaymentQuery,
  useFindTrackingQuery,
  useGetOrderByIdQuery,
  useGetOrderListQuery
} from 'modules/order/api'
import ModalCancel from 'modules/order/components/ModalCancel/ModalCancel'
import { ModalIdentificationDocuments } from 'modules/order/components/ModalIdentificationDocuments/ModalIdentificationDocuments'
import { ModalSchedule } from 'modules/order/components/ModalSchedule/ModalSchedule'
import { ModalTerminal } from 'modules/order/components/ModalTerminal/ModalTerminal'
import { OrderAction } from 'modules/order/components/OrderAction'
import { OrderCargoBlock } from 'modules/order/components/OrderCargoBlock/OrderCargoBlock'
import { OrderDocumentsBlock } from 'modules/order/components/OrderDocumentsBlock/OrderDocumentsBlock'
import OrderHelpInfoBlock from 'modules/order/components/OrderHelpInfoBlock'
import { OrderPaymentSection } from 'modules/order/components/OrderPaymentSection'
import OrderServiceTypeInfo from 'modules/order/components/OrderServiceTypeInfo/OrderServiceTypeInfo'
import { OrderSummaryBlock } from 'modules/order/components/OrderSummaryBlock'
import { OrderTrackingBlock } from 'modules/order/components/OrderTrackingBlock/OrderTrackingBlock'
import { formattedSerialId } from 'modules/order/helpers'
import { getTotalCargoParams } from 'modules/order/helpers/cargo'
import {
  formatCargoVolume,
  formatCargoVolumeCentimeter3ToMeter3,
  formatCargoWeightGramToKilogram
} from 'modules/order/helpers/formatCargo'
import { TOrderAccess } from 'modules/order/types'
import IconArrowRight from 'remixicon/icons/Arrows/arrow-right-s-line.svg?react'
import { OrderCargoInfo, OrderItem } from 'schema'
import { DefinitionAdaptive } from 'src/components/DefinitionAdaptive'
import { pluralizer } from 'src/helpers/string'
import { OrderClientBlock } from 'src/modules/order/components'
import { formatNumber } from 'src/shared/helpers/formatNumber'
import { paymentForm } from 'src/utils/feature'

import { orderDetailRoute, orderRoute } from '../../routes'
import { OrderOperationsBlock } from '../OrderOperationsBlock/OrderOperationsBlock'

import s from './styles.module.less'

interface DefinitionSkeleteProps {
  children: ReactNode
  isLoading: boolean
}

const DefinitionSkelete: FC<DefinitionSkeleteProps> = ({ isLoading = false, children }) =>
  isLoading ? (
    <>
      <PlaceholderCell className="my-1" />
      <PlaceholderCell className="my-1 grid-cols-2" />
    </>
  ) : (
    children
  )

interface OrderDefaultDetailProps {
  order: OrderItem
}
export const OrderDefaultDetail: FC<OrderDefaultDetailProps> = ({ order }) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isLargeLaptop = useIsLargeLaptop()
  const [firstLoad, setFirstLoad] = useState(true)
  const [isShowCargoDetails, setIsShowCargoDetails] = useState(false)
  const { id } = orderDetailRoute.useParams()
  const searchParams = orderDetailRoute.useSearch()
  // @ts-ignore
  const { refetch: refetchTable } = useGetOrderListQuery('', { skip: firstLoad })
  const { refetch, isLoading, isFetching } = useGetOrderByIdQuery(id || '', {
    skip: !id
  })

  const isShowAction = some(order?.access, (current) => Boolean(current))
  const { data: schedule, isLoading: isScheduleLoading } = useFindCheckOutScheduleQuery(
    {
      departureTerminalId: order?.departure?.point?.terminal?.id || '',
      destinationTerminalId: order?.destination?.point?.terminal?.id || ''
    },
    {
      skip: !order
    }
  )
  const {
    data: tracking,
    isLoading: isTrackingLoading,
    error: trackingError,
    refetch: refetchTracking
  } = useFindTrackingQuery(order?.isCargoAccepted && order?.barcode ? order?.barcode : '', {
    skip: !order?.isCargoAccepted
  })
  const { data: docAssignment, isLoading: isDocumentAssignmentLoading } = useDownloadAssignmentQuery(id || '')
  const { data: docInvoice, isLoading: isDocumentInvoiceLoading } = useDownloadInvoiceQuery(id || '', {
    skip: !order?.isCargoAccepted
  })
  const {
    data: payment,
    isLoading: isPaymentLoading,
    refetch: refetchPayment
  } = useFindOrderPaymentQuery(order?.id ? order?.id : '', {
    skip: !order?.id
  })
  const [cancelOrder, { isLoading: isCanceling }] = useCancelOrderMutation()
  const { openModal, closeModal } = useModal()
  const [pointType, setPointType] = useState('departure')
  const [, setCargoTariffType] = useState('')

  const orderCanceled = useMemo(() => order?.status === OrderItem.Status.CANCEL, [order])

  useOnMount(() => {
    refetch()
    tracking ? refetchTracking() : ''
    payment ? refetchPayment() : ''
    setFirstLoad(false)

    openModal(MODAL_IDS.DetailOrder)
  })

  const onClose = () => {
    refetchTable()
    navigate({ to: orderRoute.to, search: (prev) => ({ ...prev, ...searchParams }) })
    closeModal(MODAL_IDS.DetailOrder)
  }

  const handleOpenModalTerminal = (type: string) => {
    openModal(MODAL_IDS.OrderCreateTerminal)

    setPointType(type)
  }

  const handleOpenModalSchedule = () => {
    openModal(MODAL_IDS.OrderCreateTerminalDetail)
  }

  const handleCloseModalSchedule = () => {
    closeModal(MODAL_IDS.OrderCreateTerminalDetail)
  }

  const handleOpenModalCargo = () => {
    openModal(MODAL_IDS.OrderServiceTypeInfoDetail)
  }

  const handleCloseModalCargo = () => {
    closeModal(MODAL_IDS.OrderServiceTypeInfoDetail)
  }

  const handleSetCargoTariffType = (tariff: string) => {
    setCargoTariffType(tariff)
  }

  const handleOpenModalIdentificationDocuments = () => {
    openModal(MODAL_IDS.IdentificationDocumentsDetail)
  }

  const handleCloseModalIdentificationDocuments = () => {
    closeModal(MODAL_IDS.IdentificationDocumentsDetail)
  }

  const handleOpenModalCancel = useCallback(() => {
    openModal(MODAL_IDS.CancelOrder)
  }, [openModal])

  const handleCopyOrderTracking = (barcode: string) => {
    const link = `${getEnv('VITE_REACT_APP_LANDING_URL')}/tracking/${barcode}`
    navigator.clipboard.writeText(link).then(() => {
      notify({ type: 'success', title: 'Ссылка на историю перевозки скопирована' })
    })
  }

  const handleToggle = useCallback(() => {
    setIsShowCargoDetails(!isShowCargoDetails)
  }, [isShowCargoDetails])

  const isShowHelpInfo = (): boolean => {
    const isCourier = order?.departure?.point.isCourier
    const courierPassStatuses: string[] = [
      OrderItem.Status.NEW,
      OrderItem.Status.DEPARTURE_COURIER_ASSIGNED,
      OrderItem.Status.DEPARTURE_COURIER
    ]
    if (order?.status) {
      if (isCourier) {
        return courierPassStatuses.includes(order.status)
      }

      return order.status === OrderItem.Status.NEW
    }

    return false
  }

  const commonProps = {
    id,
    isDialogType: true,
    isCloseOnEsc: true,
    isCloseOnOutsideClick: true,
    onClose
  }

  const orderDocumentsBlock = (
    <OrderDocumentsBlock
      access={order?.access}
      barcode={order?.isCargoAccepted ? order?.barcode : undefined}
      docAssignment={docAssignment}
      docInvoice={docInvoice}
      isDocumentAssignmentLoading={isDocumentAssignmentLoading || isFetching}
      isDocumentInvoiceLoading={isDocumentInvoiceLoading || isFetching}
      isLoading={isLoading || isFetching}
      isShowFullInfo={!orderCanceled}
      serialId={order?.serialId}
    />
  )

  const orderTrackingBlock = (
    <OrderTrackingBlock
      barcode={order?.isCargoAccepted ? order?.barcode : undefined}
      departureDate={order?.departure?.departureDate}
      departurePoint={order?.departure?.point}
      destinationCourierDate={order?.destination?.destinationCourierDate}
      destinationDate={order?.destination?.destinationDate}
      destinationPoint={order?.destination?.point}
      isLoading={isLoading || isFetching}
      isShowFullInfo={!orderCanceled}
      isTrackingLoading={isTrackingLoading || isFetching}
      onOpenModalTerminal={handleOpenModalTerminal}
      tracking={tracking}
    />
  )

  const orderPartnersBlock = (
    <div className={s.orderPartnersBlock}>
      <OrderClientBlock
        isLoading={isLoading || isFetching}
        orderClientCompany={order?.departure?.partner.company}
        orderClientContact={order?.departure?.partner.contact}
        orderClientType={order?.departure?.partner.type}
        title="Отправитель"
      />
      <OrderClientBlock
        isLoading={isLoading || isFetching}
        orderClientCompany={order?.destination?.partner.company}
        orderClientContact={order?.destination?.partner.contact}
        orderClientType={order?.destination?.partner.type}
        title="Получатель"
      />
      <Collapse
        hideLabelName="Свернуть реквизиты плательщика и заказчика"
        showLabelName="Развернуть реквизиты плательщика и заказчика"
      >
        <div className="mt-4">
          <OrderClientBlock
            isLoading={isLoading || isFetching}
            orderClientCompany={order?.payer.company}
            orderClientContact={order?.payer.contact}
            orderClientType={order?.payer.type}
            title="Плательщик"
          />
          <OrderClientBlock
            isLoading={isLoading || isFetching}
            orderClientCompany={order?.customer.company}
            orderClientContact={order?.customer.contact}
            orderClientType={order?.customer.type}
            title="Заказчик"
          />
        </div>
      </Collapse>
    </div>
  )

  const orderTotalCargoBlock = useMemo(() => {
    const { cargoInfo } = order || {}
    const { height, amount, length, price, width } = cargoInfo?.summary || cargoInfo?.total || {}
    const { weight, volume } = getTotalCargoParams({
      cargoType: cargoInfo?.type,
      cargoTotal: cargoInfo?.total,
      cargo: cargoInfo?.separate
    })
    const { maxWeight } = cargoInfo?.total || {}

    return (
      <>
        <div className="mt-7 text-headline-sm">Описание груза</div>
        <div className="mt-4 grid max-w-xl grid-cols-[160px_auto] gap-x-4 rounded-md border border-outline p-3">
          <DefinitionAdaptive valueClassName="col-span-2 md:col-span-1">
            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>Общий вес</DefinitionLabel>
              <DefinitionValue>{formatCargoWeightGramToKilogram(weight)}</DefinitionValue>
            </DefinitionSkelete>
            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>Общий объём</DefinitionLabel>
              <DefinitionValue>
                {cargoInfo?.type === OrderCargoInfo.type.SEPARATE
                  ? formatCargoVolumeCentimeter3ToMeter3(volume)
                  : formatCargoVolume(volume)}
              </DefinitionValue>
            </DefinitionSkelete>

            <Divider className="col-span-2 my-1" />

            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>Самое длинное</DefinitionLabel>
              <DefinitionValue>{length} см</DefinitionValue>
            </DefinitionSkelete>

            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>Самое широкое</DefinitionLabel>
              <DefinitionValue>{width} см</DefinitionValue>
            </DefinitionSkelete>

            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>Самое высокое</DefinitionLabel>
              <DefinitionValue>{height} см</DefinitionValue>
            </DefinitionSkelete>

            {cargoInfo?.type === OrderCargoInfo.type.TOTAL && maxWeight && (
              <DefinitionSkelete isLoading={isLoading || isFetching}>
                <DefinitionLabel>Самое тяжёлое</DefinitionLabel>
                <DefinitionValue>{formatCargoWeightGramToKilogram(maxWeight)}</DefinitionValue>
              </DefinitionSkelete>
            )}

            <Divider className="col-span-2 my-1" />

            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>Оценочная стоимость</DefinitionLabel>
              <DefinitionValue>{formatNumber({ value: price, precision: 2 })} ₽</DefinitionValue>
            </DefinitionSkelete>

            <Divider className="col-span-2 my-1" />

            <DefinitionSkelete isLoading={isLoading || isFetching}>
              <DefinitionLabel>
                <span className="text-title-sm text-on-surface">Всего позиций</span>
              </DefinitionLabel>
              <DefinitionValue>
                <div className="flex gap-x-2">
                  <span className="text-title-sm">
                    {amount} {pluralizer(amount || 0, 'место', 'места', 'мест')}
                  </span>

                  {cargoInfo?.type === OrderCargoInfo.type.SEPARATE && (
                    <div className="link cursor-pointer select-none" onClick={handleToggle}>
                      {isShowCargoDetails ? 'Свернуть' : 'Развернуть'}
                    </div>
                  )}
                </div>
              </DefinitionValue>
            </DefinitionSkelete>
          </DefinitionAdaptive>
        </div>
        {isShowCargoDetails && (
          <>
            <OrderCargoBlock
              cargo={cargoInfo?.separate}
              cargoTotal={cargoInfo?.total}
              cargoType={cargoInfo?.type}
              handleOpenModalCargo={handleOpenModalCargo}
              handleSetCargoTariffType={handleSetCargoTariffType}
              isLoading={isLoading || isFetching}
            />
            <Button
              className="mb-6 mt-2 !text-label-lg"
              onClick={handleToggle}
              prefix={<IconArrowRight className="-rotate-90" size="xs" />}
              variant="outline"
            >
              Свернуть грузоместа
            </Button>
          </>
        )}
      </>
    )
  }, [order, handleOpenModalCargo, handleToggle, isFetching, isLoading, isShowCargoDetails])

  const orderSummaryBlock = (
    <OrderSummaryBlock
      isOrderDetail
      cargoInfo={order?.cargoInfo}
      isLoading={isLoading || isFetching}
      isScheduleLoading={isScheduleLoading || isFetching}
      isShowFullInfo={!orderCanceled}
      onOpenModalSchedule={handleOpenModalSchedule}
      schedule={schedule}
      trackingError={trackingError}
    />
  )

  const orderCostBlock = (
    <OrderOperationsBlock
      departureTerminal={order.departure.point.terminal}
      destinationTerminal={order.destination.point.terminal}
      isCargoAccepted={order?.isCargoAccepted}
      isLoading={isLoading || isFetching}
      operations={order.operation || []}
      totalPrice={order?.totalPrice}
    />
  )

  const orderHelpInfoBlock = (
    <>
      {!order?.isCargoAccepted && isShowHelpInfo() ? (
        <OrderHelpInfoBlock
          cancelInfo={order?.cancelInfo}
          departureDate={order?.departure?.departureDate}
          departurePartner={order?.departure?.partner}
          departurePointTerminal={order?.departure?.point.terminal}
          handleOpenModalIdentificationDocuments={handleOpenModalIdentificationDocuments}
          isCourier={order?.departure?.point.isCourier}
          isLoading={isFetching}
          onOpenModalTerminal={handleOpenModalTerminal}
          status={order?.status}
        />
      ) : (
        ''
      )}
      {!order?.isCargoAccepted && order?.status === OrderItem.Status.CANCEL ? (
        <OrderHelpInfoBlock
          cancelInfo={order?.cancelInfo}
          departureDate={order?.departure?.departureDate}
          departurePartner={order?.departure?.partner}
          departurePointTerminal={order?.departure?.point.terminal}
          handleOpenModalIdentificationDocuments={handleOpenModalIdentificationDocuments}
          isCourier={order?.departure?.point.isCourier}
          isLoading={isFetching}
          status={order?.status}
        />
      ) : (
        ''
      )}
    </>
  )

  const orderPaymentBlock = paymentForm && (
    <OrderPaymentSection
      docInvoice={docInvoice}
      isDocumentInvoiceLoading={isDocumentInvoiceLoading}
      isFetching={isFetching}
      isPaymentLoading={isPaymentLoading}
      order={order}
      payment={payment}
    />
  )

  const content = (
    <div className="overflow-hidden pb-10 pl-16 pr-6 max-[963px]:pl-0 max-[963px]:pr-0 max-[963px]:pt-1">
      <div className={s.detailBody}>
        <div className={cn(s.left, '')}>
          {isTablet && order?.barcode ? (
            <div className={s.barcode}>
              Трек № {order?.barcode}
              <IconButton onClick={() => handleCopyOrderTracking(order?.barcode ? order?.barcode : '')} size="nm">
                <IconCopy size="nm" />
              </IconButton>
            </div>
          ) : (
            ''
          )}
          {orderHelpInfoBlock}
          {orderDocumentsBlock}
          {orderTrackingBlock}
          {orderPartnersBlock}
          {orderTotalCargoBlock}
          {!isLargeLaptop && order?.comment ? (
            <div className={s.orderCommentBlock}>
              <h2 className={s.outerTitle}>Дополнительная информация</h2>
              <div className={s.commentText}>{order?.comment}</div>
            </div>
          ) : (
            ''
          )}
        </div>
        <div className={cn(s.right, 'mb-4')}>
          {orderSummaryBlock}
          {orderCostBlock}
          {orderPaymentBlock}
          {isLargeLaptop && order?.comment ? (
            <div className={s.orderCommentBlock}>
              <h2 className={s.outerTitle}>Дополнительная информация</h2>
              <div className={s.commentText}>{order?.comment}</div>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  )

  const desktopHeader = (
    <>
      {isTablet ? (
        <ModalHeader isSticky className={cn(s.detailHeader, s.detailHeaderTablet)}>
          <div className={cn(s.modalTitle, s.modalDetailTitle)}>
            {isLoading || isFetching ? (
              <PlaceholderCell height={32} />
            ) : (
              <div className={s.mobileCloseTitle}>
                <IconClose className={s.mobileCloseIcon} onClick={onClose} />
                <div className="ml-8 mr-4">Заявка {formattedSerialId(order?.serialId)}</div>
              </div>
            )}
            {isShowAction && (
              <OrderAction access={order?.access as TOrderAccess} id={id} onCancel={handleOpenModalCancel} />
            )}
          </div>
        </ModalHeader>
      ) : (
        <ModalHeader isOrder isSticky className={s.detailHeader}>
          <div className={cn(s.modalTitle, s.modalDetailTitle)}>
            <ButtonIcon className={s.closeIcon} color="white" icon={IconModalClose} onClick={onClose} />
            {isLoading || isFetching ? (
              <PlaceholderCell height={32} />
            ) : (
              order && <div className="ml-8 mr-4">Заявка {formattedSerialId(order.serialId)}</div>
            )}
            {isShowAction && (
              <OrderAction access={order?.access as TOrderAccess} id={id} onCancel={handleOpenModalCancel} />
            )}
          </div>
          {Boolean(order?.barcode) && (
            <div className={cn(s.barcode, '')}>
              Трек № {order?.barcode}
              <IconButton onClick={() => handleCopyOrderTracking(order?.barcode ? order?.barcode : '')} size="lg">
                <IconCopy size="nm" />
              </IconButton>
            </div>
          )}
        </ModalHeader>
      )}
    </>
  )

  const desktop = (
    <ModalDesktop {...commonProps} isOrder className="z-[999]" header={desktopHeader} isCloseButton={false} size="xxl">
      {content}
    </ModalDesktop>
  )

  const mobileHeader = (
    <ModalHeader className={s.detailHeader}>
      <div className={cn(s.modalTitle, s.modalDetailTitle)}>
        {isLoading || isFetching ? (
          <PlaceholderCell height={32} />
        ) : (
          <div className={s.mobileCloseTitle}>
            <IconClose className={s.mobileCloseIcon} onClick={onClose} />
            <div className="ml-8 mr-4">Заявка {formattedSerialId(order?.serialId)}</div>
            {isShowAction && (
              <OrderAction access={order?.access as TOrderAccess} id={id} onCancel={handleOpenModalCancel} />
            )}
          </div>
        )}
      </div>
    </ModalHeader>
  )

  const mobile = (
    <ModalMobile className={s.fullSize} {...commonProps} header={mobileHeader} isShowCloseButton={false}>
      {content}
    </ModalMobile>
  )

  const terminal = pointType === 'departure' ? order?.departure?.point.terminal : order?.destination?.point.terminal

  return (
    <>
      <Seo
        description="Детальная информация о заявке, размещенной в сервисе QP5 Express: данные заказчика и плательщика, описание груза, стоимость перевозки и история перемещения."
        title="Просмотр заявки – сборные перевозки QP5 Express"
      />
      <ModalItem id={MODAL_IDS.OrderCreateTerminal}>
        <ModalSimple
          id={MODAL_IDS.OrderCreateTerminal}
          onClose={() => closeModal(MODAL_IDS.OrderCreateTerminal)}
          title={terminal?.name || ''}
        >
          <ModalTerminal terminal={terminal} />
        </ModalSimple>
      </ModalItem>
      <ModalItem id={MODAL_IDS.CancelOrder}>
        <ModalCancel
          cancelOrder={cancelOrder}
          focusedOrder={null}
          id={MODAL_IDS.CancelOrder}
          isSubmitting={isCanceling}
          onClose={() => closeModal(MODAL_IDS.CancelOrder)}
          orderId={order?.id}
          refetch={refetch}
          refetchTable={refetchTable}
          serialId={order?.serialId}
        />
      </ModalItem>
      <ModalItem id={MODAL_IDS.DetailOrder}>{isMobile ? mobile : desktop}</ModalItem>
      <ModalItem id={MODAL_IDS.OrderCreateTerminalDetail}>
        <ModalSchedule
          handleCloseModalSchedule={handleCloseModalSchedule}
          id={MODAL_IDS.OrderCreateTerminalDetail}
          isLoading={isScheduleLoading || isFetching}
          schedule={schedule}
        />
      </ModalItem>
      <ModalItem id={MODAL_IDS.OrderServiceTypeInfoDetail}>
        <OrderServiceTypeInfo handleClose={handleCloseModalCargo} id={MODAL_IDS.OrderServiceTypeInfoDetail} />
      </ModalItem>
      <ModalItem id={MODAL_IDS.IdentificationDocumentsDetail}>
        <ModalIdentificationDocuments
          handleCloseModalIdentificationDocuments={handleCloseModalIdentificationDocuments}
          id={MODAL_IDS.IdentificationDocumentsDetail}
        />
      </ModalItem>
    </>
  )
}
